import React, { useMemo, useState } from 'react'
import { Bot, ChevronLeft, ChevronRight, MoreHorizontal, Plus, Sparkles } from 'lucide-react'
import { ExcelRibbon, type ExcelRibbonVariant } from './ExcelRibbon'
import { ExcelStatusBar } from './ExcelStatusBar'
import { getFuseGridButtonIcon } from '../icons/FuseGridButtonIcons'
import '../../styles/quadraticCreatorShell.css'
import './ExcelWorkbookChrome.css'

type MockupVariant = {
  id: ExcelRibbonVariant
  label: string
  description: string
}

const VARIANTS: MockupVariant[] = [
  {
    id: 'standard',
    label: 'Layout A: Excel Web Standard',
    description: 'Full title/search strip, tabbed ribbon, formula row, grid, sheet tabs, status bar.',
  },
  {
    id: 'compact',
    label: 'Layout B: Compact Excel Web',
    description: 'Dense chrome for smaller screens with icon-first ribbon commands.',
  },
  {
    id: 'power',
    label: 'Layout C: FuseGrid Power Workbook',
    description: 'Excel-like workbook shell plus a right-side data and AI command pane.',
  },
]

const CancelIcon = getFuseGridButtonIcon('close')!
const AcceptIcon = getFuseGridButtonIcon('accept')!
const ExpandIcon = getFuseGridButtonIcon('chevron-down')!

const sampleValues = [
  ['Region', 'Rep', 'Product', 'Q1', 'Q2', 'Q3', 'Q4', 'Total', 'Forecast', 'Notes', '', ''],
  ['West', 'Avery', 'Widgets', '1240', '1430', '1550', '1630', '5850', '6100', 'On track', '', ''],
  ['East', 'Morgan', 'Gears', '980', '1120', '1180', '1210', '4490', '4700', 'Needs review', '', ''],
  ['South', 'Riley', 'Bolts', '1410', '1495', '1510', '1680', '6095', '6320', 'Strong', '', ''],
  ['North', 'Casey', 'Panels', '760', '820', '910', '950', '3440', '3590', '', '', ''],
  ['', '', '', '', '', '', '', '', '', '', '', ''],
  ['Formula', '=SUM(D2:G5)', '', '', '', '', '', '=XLOOKUP(B3,B2:B5,H2:H5)', '', '', '', ''],
]

function initialVariant(): ExcelRibbonVariant {
  if (typeof window === 'undefined') return 'standard'
  const raw = String(new URLSearchParams(window.location.search).get('variant') || '').toLowerCase()
  if (raw === 'b' || raw === 'compact') return 'compact'
  if (raw === 'c' || raw === 'power') return 'power'
  return 'standard'
}

function MockFormulaRow({ variant }: { variant: ExcelRibbonVariant }) {
  const formula = variant === 'power'
    ? '=XLOOKUP(B3, Sales[Rep], Sales[Total], "not found", 0, 2)'
    : '=SUM(D2:G5)'
  return (
    <div className="fg-excel-chrome fg-excel-formula-row" data-testid="fg-excel-mock-formula-row">
      <label className="fg-excel-name-box" title="Name Box">
        <input value={variant === 'compact' ? 'H7' : 'B2:E5'} readOnly aria-label="Name Box" title="Name Box" />
      </label>
      <div className="fg-excel-formula-controls">
        <button type="button" className="fg-excel-icon-button" title="Cancel formula edit" aria-label="Cancel formula edit" disabled><CancelIcon size={16} /></button>
        <button type="button" className="fg-excel-icon-button" title="Accept formula edit" aria-label="Accept formula edit" disabled><AcceptIcon size={16} /></button>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          border: '1px solid #cfd5dd',
          borderRadius: 4,
          background: '#fff',
          padding: '0 9px',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: 13,
        }}
        title="Formula bar"
      >
        <span style={{ color: '#667085', fontStyle: 'italic', fontWeight: 800, marginRight: 12 }}>fx</span>
        <span style={{ color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {formula}
        </span>
      </div>
      <div className="fg-excel-formula-expand">
        <button type="button" className="fg-excel-icon-button" title="Expand formula bar" aria-label="Expand formula bar" disabled><ExpandIcon size={16} /></button>
      </div>
    </div>
  )
}

function MockGrid({ variant }: { variant: ExcelRibbonVariant }) {
  const hasSidecar = variant === 'power'
  const rows = Array.from({ length: 22 }, (_, index) => index + 1)
  const cols = 'ABCDEFGHIJKL'.split('')
  const cells = Array.from({ length: 22 * 12 }, (_, index) => {
    const row = Math.floor(index / 12)
    const col = index % 12
    return sampleValues[row]?.[col] ?? ''
  })
  return (
    <div className="fg-excel-mock-main" data-has-sidecar={hasSidecar ? 'true' : 'false'}>
      <div className="fg-excel-mock-corner" />
      <div className="fg-excel-mock-col-headers">
        {cols.map((col) => <span key={col}>{col}</span>)}
      </div>
      <div className="fg-excel-mock-row-headers">
        {rows.map((row) => <span key={row}>{row}</span>)}
      </div>
      <div className="fg-excel-mock-grid" data-testid="fg-excel-mock-grid">
        {cells.map((cell, index) => (
          <div
            key={index}
            className="fg-excel-mock-cell"
            style={{
              fontWeight: index < 12 ? 800 : 500,
              background: index < 12 ? '#f8fafc' : undefined,
              color: String(cell).startsWith('=') ? 'var(--fg-excel-formula-ref)' : undefined,
            }}
          >
            {cell}
          </div>
        ))}
        <div className="fg-excel-mock-selection" />
        <div className="fg-excel-mock-formula-ref" />
      </div>
      {hasSidecar && (
        <aside className="fg-excel-mock-sidecar" data-testid="fg-excel-power-sidecar">
          <h3>FuseGrid Assist</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#107c41', fontWeight: 800 }}>
            <Sparkles size={16} />
            Data-aware workbook tools
          </div>
          <div className="fg-excel-mock-sidecar-card">
            <strong>SQL range preview</strong>
            <div style={{ color: '#667085', marginTop: 4 }}>Use selected cells as `fg_input` for read-only SQL.</div>
          </div>
          <div className="fg-excel-mock-sidecar-card">
            <strong>Formula helper</strong>
            <div style={{ color: '#667085', marginTop: 4 }}>XLOOKUP, SQL(), and spill guidance appear while editing.</div>
          </div>
          <div className="fg-excel-mock-sidecar-card">
            <strong>Suggested action</strong>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
              <Bot size={15} />
              Build a forecast from Q1-Q4
            </div>
          </div>
        </aside>
      )}
    </div>
  )
}

function MockSheetTabs() {
  return (
    <div className="fg-excel-sheet-tabs" data-testid="fg-excel-sheet-tabs">
      <button type="button" title="Scroll sheets left"><ChevronLeft size={14} /></button>
      <button type="button" title="Scroll sheets right"><ChevronRight size={14} /></button>
      <button type="button" title="New sheet"><Plus size={14} /></button>
      <button type="button" data-active="true" title="Sales Model sheet">Sales Model</button>
      <button type="button" title="Inputs sheet">Inputs</button>
      <button type="button" title="Forecast sheet">Forecast</button>
      <button type="button" title="SQL Results sheet">SQL Results</button>
      <button type="button" title="Sheet actions"><MoreHorizontal size={14} /></button>
    </div>
  )
}

function MockWorkbook({ variant }: { variant: ExcelRibbonVariant }) {
  const saveState = variant === 'power' ? 'DocumentSession connected' : 'Saved'
  return (
    <div className="fg-excel-mock-workbook" data-testid="fg-excel-mock-workbook" data-variant={variant}>
      <ExcelRibbon
        mockMode
        variant={variant}
        workbookName={variant === 'power' ? 'Revenue Model - FuseGrid Power Workbook' : 'Revenue Model'}
        saveState={saveState}
        currentFormat={{ bold: true, align: 'center', backgroundColor: '#e8f3ed', textColor: '#107c41' }}
      />
      <MockFormulaRow variant={variant} />
      <MockGrid variant={variant} />
      <MockSheetTabs />
      <ExcelStatusBar
        mockSummary={{
          mode: variant === 'compact' ? 'Ready' : 'Point',
          average: '1,423.75',
          count: '16',
          numericalCount: '16',
          sum: '22,780',
        }}
      />
    </div>
  )
}

export function ExcelChromeMockups() {
  const [variant, setVariant] = useState<ExcelRibbonVariant>(() => initialVariant())
  const active = useMemo(() => VARIANTS.find((entry) => entry.id === variant) ?? VARIANTS[0], [variant])
  return (
    <main className="fg-excel-chrome fg-excel-mockup-page" data-testid="fg-excel-mockup-page">
      <div className="fg-excel-mockup-toolbar">
        <strong>Excel-like Chrome Mockups</strong>
        {VARIANTS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            data-active={entry.id === variant ? 'true' : 'false'}
            title={entry.label}
            onClick={() => setVariant(entry.id)}
          >
            {entry.label.replace(/^Layout\s/, '')}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', color: '#667085', fontSize: 12 }}>{active.description}</span>
      </div>
      <section className="fg-excel-mockup-stage">
        <MockWorkbook variant={variant} />
      </section>
    </main>
  )
}
